import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
    Box, Chip, Stack, CircularProgress, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { fetchParticipantAnswers } from '../../services/trainingSessionService';

/**
 * Loads detailed per-question answers from /api/training-sessions/{uuid}/participants/{pid}/answers.
 * Shows for each option: selected vs correct.
 */
export default function ParticipantResultModal({ open, onClose, sessionUuid, participant }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !participant) return;
        let alive = true;
        setLoading(true);
        setError(null);
        setData(null);

        fetchParticipantAnswers(sessionUuid, participant.id)
            .then(payload => {
                if (alive) setData(payload);
            })
            .catch(err => {
                if (alive) setError(err?.response?.data?.error || err.message || 'Failed to load');
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => { alive = false; };
    }, [open, participant, sessionUuid]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>
                Quiz answers — {participant?.displayName}
                {data?.attempt && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Score: {data.attempt.scoreCorrect}/{data.attempt.scoreTotal} · Language: {data.attempt.language?.toUpperCase()}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
                {error && <Alert severity="error">{error}</Alert>}
                {!loading && data?.questions && (
                    <Stack spacing={2}>
                        {data.questions.map((q, idx) => (
                            <Box key={q.id} sx={{ border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                                <Typography variant="caption" color="text.secondary">Question {idx + 1} · {q.type}</Typography>
                                <Typography fontWeight={500} sx={{ mb: 1.5 }}>{q.text}</Typography>
                                <Stack spacing={0.75}>
                                    {q.options.map(opt => {
                                        const selected = q.selectedIds?.includes(opt.id);
                                        let icon, color, label = null;
                                        if (opt.isCorrect && selected) {
                                            icon = <CheckCircleIcon fontSize="small" color="success" />;
                                            color = 'success.dark';
                                            label = <Chip label="correct ✓" color="success" size="small" />;
                                        } else if (opt.isCorrect) {
                                            icon = <CheckCircleIcon fontSize="small" sx={{ color: '#9ccc65' }} />;
                                            color = 'text.primary';
                                            label = <Chip label="correct" color="success" size="small" variant="outlined" />;
                                        } else if (selected) {
                                            icon = <CancelIcon fontSize="small" color="error" />;
                                            color = 'error.dark';
                                            label = <Chip label="selected (wrong)" color="error" size="small" />;
                                        } else {
                                            icon = <RadioButtonUncheckedIcon fontSize="small" sx={{ color: '#bbb' }} />;
                                            color = 'text.secondary';
                                        }
                                        return (
                                            <Stack key={opt.id} direction="row" alignItems="center" spacing={1}>
                                                {icon}
                                                <Typography variant="body2" sx={{ flex: 1, color }}>
                                                    {opt.text}
                                                </Typography>
                                                {label}
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
